import {Injectable} from '@angular/core';
import {OrderService} from "./OrderService";
import {ModelProvider} from "../../framework/providers/modelProvider/ModelProvider";
import {from, Observable, map, of, switchMap} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Events} from "../../framework/providers/events";
import {Storage} from '@ionic/storage-angular'; // Importing @ionic/storage
import * as pako from 'pako';
import {AppConfig} from "../conf/main";
import {AppDataService} from "./AppDataService";

export interface CompanyInterface {
    id: number;
    created: string;
    agentName: string;
    companyName: string;
    internalCompanyId: string;
    companyFileNumber: string;
    cellphone: string;
    prices_last_sync: string;

    approved: boolean;
    priceApproved: boolean;
    prices_data?: object; // Optional to reflect the removal
}

export interface MiniCompany {
    data: CompanyInterface;
}

export interface SyncLogEntry {
    date: string;
    trigger: string; // 'auto' | 'manual' | 'resume'
    modelsCount?: number;
}

export interface StorageData {
    sync_date: string;
    lastServerSync: string; // ISO string from server, used for comparison
    data_updated: boolean;
    includePricesData: boolean;
    modelsCount: number;
    syncLog?: SyncLogEntry[];
}

export interface CompanyServiceOptions {
    loading: boolean;
}

const eventKey = 'companiesList'

@Injectable({
    providedIn: 'root'
})
export class CompanyService {

    private appuser;
    private localStorageKey: string = 'userCompanies';
    private filters: object = {'setPrefix': true, 'limit': 20, 'offset':0};
    //
    private storageData;
    private options: CompanyServiceOptions;

    private initmodels;

    constructor(private orderService: OrderService,
                private modelProvider: ModelProvider,
                private appDataService: AppDataService,
                public events: Events,
                private storage: Storage) {
        this.storage.create().then(() => {
            // this.start();
        });
    }

    start() {
        this.setAppUser();
        this.setStorageData().then(() => {
            this.options = { loading: false };
            this.scheduleDailySync(); // Schedule automatic daily sync check
        });

        this.handleAppLifecycleEvents(); // Ensure sync runs when app is resumed
    }

    private scheduleDailySync() {
        this.checkServerAndSync();

        setInterval(() => {
            this.checkServerAndSync();
        }, 60 * 60 * 1000); // Check every 15 minutes
    }

    private checkServerAndSync() {
        if (this.options?.loading) return;

        this.getServerLastSync().then(serverLastSync => {
            if (!serverLastSync) {
                console.log("Could not get server sync date, skipping sync check.");
                return;
            }

            const localLastSync = this.storageData?.lastServerSync || null;

            if (!localLastSync || new Date(serverLastSync) > new Date(localLastSync)) {
                console.log("Server has newer data, triggering sync...", {
                    server: serverLastSync,
                    local: localLastSync || 'none'
                });
                this.listCompanies(this.filters, true, 'auto').subscribe(() => {
                    console.log("Sync completed.");
                    this.reloadCurrentCompany();
                });
            } else {
                console.log("Local data is up to date.", {
                    server: serverLastSync,
                    local: localLastSync || 'none'
                });
            }
        }).catch(err => {
            console.error("Error checking server sync date:", err);
        });
    }

    private getServerLastSync(): Promise<string | null> {
        return this.modelProvider.custom('getAppdataPricesLastSync', { 'setPrefix': true }).then(res => {
            if (res && res['last_sync']) {
                return res['last_sync'];
            }
            return null;
        });
    }

    private reloadCurrentCompany() {
        const companyId = this.appuser?.data?.companyId;
        if (!companyId) return;

        this.getMiniCompany(companyId).then(company => {
            if (!company) return;

            console.log("Reloading current company with updated prices:", companyId);
            let prefix = 'sync_reload_' + Math.floor(Math.random() * 1000000);

            if (company.data.prices_data) {
                let params = {'prefix': prefix, 'date': company.data.prices_last_sync, 'disable_prices_list': true};
                this.appDataService.agentCompanyChanged(params, company.data.prices_data);
            }

            this.orderService.changeAgentCompany(company, null, true, prefix, true);
        });
    }

    private handleAppLifecycleEvents() {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                console.log("App resumed, checking sync...");
                this.checkServerAndSync();
            }
        });
    }

    subscribes() {
        this.events.subscribe(eventKey + '_nextPage', (res) => {
            console.log(res);
        });
    }

    setAppUser() {
        this.appuser = this.orderService.getAppUser();
        this.setStorageKey();
    }

    setStorageKey() {
        this.localStorageKey = 'userCompanies_10'// + Math.floor(Math.random() * Math.floor(1000000)); //+ this.appuser.data.id;
    }


    listCompanies(filters = this.filters, saveInStorage=true, syncTrigger='manual'): Observable<object> {
        if(!this.appuser){
            this.setAppUser();
        }
        if(!this.appuser){
            return
        }

        this.options.loading = true;

        filters['include_price_data'] = this.storageData.includePricesData;

        if(filters['search'])
            saveInStorage = false;

        let url = 'getAppUserCompaniesData';
        let domain = '';
        let appuser_key = this.appuser.data['prices_file_key'];
        if(!appuser_key)
        {
            appuser_key = 'appuser_';

            if(this.orderService.isStaff()){
                appuser_key += 'staff'
            }
            else{
                appuser_key += this.appuser.data.id
            }
        }

        console.log('appuser_key', appuser_key)

        if(filters['include_price_data']) {
            if (AppConfig.cdnData) {
                url = AppConfig.CDN_URL_DOMAIN_PREFIX  + '/agents_prices/' +appuser_key + '.txt';
                domain = AppConfig.CDN_URL_DOMAIN;
            }
        }else{
            url = 'minicompany'
        }

        return from(this.modelProvider.custom(url, filters , domain)).pipe(
            map(res => {
                let data;

                if(filters['include_price_data']) {
                    data = decompressData(res['res'])
                }
                else{
                    data = {'models':res['results'], 'modelsCount': res['count']};
                }

                this.options.loading = false;

                data['models'] = data['models'].map((model: any): MiniCompany => ({data: model}))
                return data;
            }),
            switchMap(data => saveInStorage ? from(this.setCompanies(data['models'])).pipe(
                tap(() => {
                    this.storageData.modelsCount = data['modelsCount']
                    this.updateSyncDate(syncTrigger);
                    console.log("Storage Updated!", this.storageData);
                }),
                map(() => data)  // Return companies after they've been saved
            ) : of(data))
        );
    }

    getCompanies(filters: { offset?: number; limit?: number }, init = false): Observable<object> {
        if (filters['search'] && !this.storageData.includePricesData) {
            return this.listCompanies(filters);
        }

        if (init) {
            return this.listCompanies().pipe(
                switchMap(() => from(this.getStoredCompanies(filters)))
            );
        }

        return from(this.getStoredCompanies(filters)).pipe(
            switchMap(res => {
                if (res) {
                    return of(res);
                } else {
                    return this.listCompanies().pipe(
                        switchMap(() => from(this.getStoredCompanies(filters)))
                    );
                }
            })
        );
    }

    getInitModels()
    {
        return this.initmodels;
    }

    resetData(){
        let storageKey = this.localStorageKey + '_storageData';
        this.storage.set(storageKey, null);
        this.setCompanies([]);
    }

    setCompanies(data: MiniCompany[]): Promise<void> {
        const minidata = this.toMiniData(data.map(item => item.data)); // Convert to mini data

        this.initmodels = null;
        for(let model of data){
            let com_key = this.localStorageKey+'_company_id:'+model.data['id']
            this.storage.set(com_key, model);
        }

        return this.storage.set(this.localStorageKey+'_minidata', minidata);
    }

    toMiniData(companies: CompanyInterface[]): MiniCompany[] {
        return companies.map(company => {
            const { prices_data, ...rest } = company;

            return {
                data: rest
            };
        });
    }


    private getStoredCompanies(filters: { offset?: number; limit?: number; search?: string }, useMiniData=true): Promise<object> {
        let localStorageKey = this.localStorageKey
        if(useMiniData)
            localStorageKey += '_minidata';

        return this.storage.get(localStorageKey).then(data => {
            if (!data) return null;
            // if(!this.storageData['data_updated']) return null;

            let all_companies: MiniCompany[] = data;
            if (filters['search']) {
                all_companies = this.searchCompanies(all_companies, filters['search']);
            }

            let companies = [];
            // Apply offset and limit if defined
            if (filters.offset !== undefined && filters.limit !== undefined) {
                companies = all_companies.slice(filters.offset, filters.offset + filters.limit);
            }

            if(!this.initmodels) this.initmodels = companies;

            return {'models': companies, 'modelsCount': all_companies.length};
        });
    }

    updateCompanies(companyData: MiniCompany): Observable<MiniCompany[]> {
        return from(this.storage.get(this.localStorageKey)).pipe(
            map(companies => {
                const index = companies.findIndex(c => c.data.id === companyData.data.id);
                if (index !== -1) {
                    companies[index] = {...companies[index], ...companyData};
                    this.storage.set(this.localStorageKey, companies);
                }
                return companies;
            })
        );
    }

    setDefaultCompany(){
        return // not good, makes the app relist business and appdata.
        // let company_settings = this.appDataService.getPricesListKey('company_settings');
        // if(company_settings){
        //     this.getMiniCompany(company_settings['default_company']).subscribe(res=>{
        //         this.orderService.setDefaultCompany(res);
        //     })
        // }
    }

    nextPage(filters): Observable<any>{
        if(this.storageData.includePricesData)
        {
            return this.getCompanies(filters);
        }
        return this.listCompanies(filters,false);
    }

    getMiniCompany(companyId: number): Promise<MiniCompany> {
        let com_key = this.localStorageKey+'_company_id:'+companyId
        this.appDataService.options['loading'] = true;

        return this.storage.get(com_key).then(res=>{
            this.appDataService.options['loading'] = false;

            return res;
        })
    }

    searchCompanies(companies: MiniCompany[], searchTxt: string) {
        const searchLower = searchTxt.toLowerCase();
        return companies.filter(c =>
            c.data.companyName.toLowerCase().includes(searchLower) ||
            c.data.id.toString().includes(searchLower) ||
            c.data.internalCompanyId && c.data.internalCompanyId.includes(searchLower) ||
            c.data.companyFileNumber && c.data.companyFileNumber.includes(searchLower)
        );
    }

    setStorageData(): Promise<StorageData> {
        let storageKey = this.localStorageKey + '_storageData';
        return this.storage.get(storageKey).then(data => {
            if (data) {
                data['data_updated'] = !!data['sync_date']
                this.storageData = data;

                this.setDefaultCompany()
            } else {
                this.storageData = {'sync_date': null, 'data_updated': false};
                this.storage.set(storageKey, this.storageData);
            }

            return this.storageData;
        });
    }

    updateSyncDate(trigger: string = 'manual') {
        let storageKey = this.localStorageKey + '_storageData';
        this.storageData.sync_date = getDateStr();
        this.storageData.data_updated = true;
        this.storageData.lastServerSync = new Date().toISOString();

        // Add sync log entry
        if (!this.storageData.syncLog) this.storageData.syncLog = [];
        this.storageData.syncLog.unshift({
            date: this.storageData.sync_date,
            trigger: trigger,
            modelsCount: this.storageData.modelsCount
        });
        if (this.storageData.syncLog.length > 20) {
            this.storageData.syncLog = this.storageData.syncLog.slice(0, 20);
        }

        this.storage.set(storageKey, this.storageData);
    }

    getSyncLog(): SyncLogEntry[] {
        return this.storageData?.syncLog || [];
    }

    getStorageData() {
        return this.storageData;
    }

    getOptions() {
        return this.options;
    }
}

function getDateStr() {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
    return formattedDate;
}


function decompressData(compressedData) {
    // Decode the base64-encoded string
    if (isValidBase64(compressedData)) {
        const decodedData = atob(compressedData);

        // Convert the decoded string to a Uint8Array
        const uint8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; i++) {
            uint8Array[i] = decodedData.charCodeAt(i);
        }

        // Decompress the data using pako
        const decompressedData = pako.inflate(uint8Array, {to: 'string'});

        // Now, decompressedString contains the original JSON data
        return JSON.parse(decompressedData);
    }
    return compressedData
}


function isValidBase64(str) {
    try {
        return btoa(atob(str)) == str;
    } catch (e) {
        return false;
    }
}
